import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { JT_APP_URL } from '../../api/client';
import { useAuthStore } from '../../stores/auth.store';
import { CardFooter, HeaderLogos, IconBadge, PrimaryButton } from './SessionErrorPage';

/**
 * Shown when Engage refuses or ends a session for an access reason rather
 * than an expiry: the JT → Engage exchange was refused, access was switched
 * off in JobTetris mid-session, or a Manage as / takeover session ended.
 * `?reason=<code>` picks the copy. Routed outside AuthGuard; always clears
 * this tab's local session on mount.
 */

interface ReasonCopy {
  title: string;
  body: string;
  tone: 'warning' | 'danger';
  icon: 'lock' | 'check';
  /** Offer "Close this tab" (impersonation tabs are usually script-opened). */
  sessionEnded?: boolean;
}

const REASON_COPY: Record<string, ReasonCopy> = {
  ENGAGE_ACCESS_NOT_GRANTED: {
    title: 'You don’t have access to Engage yet',
    body: 'Engage access is turned on per user by your JobTetris admin. Ask them to enable it for you in JobTetris → Settings → Users → Engage, then open Engage again.',
    tone: 'warning',
    icon: 'lock',
  },
  ENGAGE_ACCESS_REVOKED: {
    title: 'Your Engage access has been turned off',
    body: 'Your JobTetris admin has switched off Engage for your account, so you’ve been signed out. Contact your JobTetris admin if this is unexpected.',
    tone: 'danger',
    icon: 'lock',
  },
  ENGAGE_NOT_ENABLED_FOR_ORG: {
    title: 'Engage isn’t part of your plan',
    body: 'Your organisation’s JobTetris subscription doesn’t include Engage. Speak to your JobTetris account admin about adding Engage.',
    tone: 'warning',
    icon: 'lock',
  },
  MANAGE_AS_ACTOR_NOT_ADMIN: {
    title: 'This Manage as session has ended',
    body: 'You no longer have Engage admin access, so this session was closed. You can close this tab.',
    tone: 'warning',
    icon: 'lock',
    sessionEnded: true,
  },
  MANAGE_AS_ENDED: {
    title: 'Session ended: you can close this tab',
    body: 'Your Manage as session is over. Everything you did was recorded as you, on their behalf.',
    tone: 'warning',
    icon: 'check',
    sessionEnded: true,
  },
  TAKEOVER_ENDED: {
    title: 'Session ended: you can close this tab',
    body: 'The takeover session is over.',
    tone: 'warning',
    icon: 'check',
    sessionEnded: true,
  },
};

const DEFAULT_COPY: ReasonCopy = {
  title: 'You can’t use Engage right now',
  body: 'Engage couldn’t open a session for your account. Head back to JobTetris and launch Engage again, or contact your JobTetris admin if this keeps happening.',
  tone: 'warning',
  icon: 'lock',
};

function jtHost(): string {
  try {
    return new URL(JT_APP_URL).host;
  } catch {
    return JT_APP_URL;
  }
}

export function NoAccessPage() {
  const [params] = useSearchParams();
  const reason = params.get('reason') ?? '';
  const copy = Object.prototype.hasOwnProperty.call(REASON_COPY, reason) ? REASON_COPY[reason] : DEFAULT_COPY;

  useEffect(() => {
    // Whatever brought us here, this tab's session is over. Callers only wipe
    // the persisted copy before navigating (so AuthGuard doesn't bounce them
    // first); the in-memory state is cleared here.
    useAuthStore.getState().clearAuth();
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: 'var(--color-surface-page)',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <header
        style={{
          height: 'var(--topbar-height)',
          backgroundColor: 'var(--topbar-bg)',
          borderBottom: '1px solid var(--topbar-border)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 var(--space-6)',
          flexShrink: 0,
        }}
      >
        <HeaderLogos />
      </header>

      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-10) var(--space-6)',
        }}
      >
        <article
          style={{
            width: '100%',
            maxWidth: '520px',
            backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 'var(--card-radius)',
            boxShadow: 'var(--card-shadow)',
            padding: 'var(--card-padding)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--space-4)',
          }}
        >
          <IconBadge tone={copy.tone}>
            {copy.icon === 'check' ? <IconCheckCircle size={22} /> : <IconLock size={22} />}
          </IconBadge>

          <h2
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--color-text-brand)',
              letterSpacing: 'var(--tracking-tight)',
              lineHeight: 'var(--leading-tight)',
            }}
          >
            {copy.title}
          </h2>

          <p
            style={{
              fontSize: 'var(--text-md)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--leading-relaxed)',
            }}
          >
            {copy.body}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
            <PrimaryButton href={JT_APP_URL}>Return to JobTetris</PrimaryButton>
            {copy.sessionEnded && (
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  window.close();
                }}
                style={{
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-link)',
                  textDecoration: 'none',
                }}
              >
                Close this tab
              </a>
            )}
          </div>

          <CardFooter>
            {jtHost()}
            {reason ? ` · ${reason}` : ''}
          </CardFooter>
        </article>
      </main>
    </div>
  );
}

/* ── Icons (Feather) ── */

function IconLock({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function IconCheckCircle({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}
